import { Box, Button, LinearProgress, Tooltip, Typography } from '@mui/material'
import {
    generatePDFReportForEntity,
    fetchTimeseriesTableData,
} from '../../../../services/entityServices'
import { findEntityById } from '../../../../utils/EntityUtils'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { AttributeType } from '../../../../constants/constants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
    ChangeEvent,
} from 'react'
import RealTimeAlertEventsCheckbox from '../../Components/RealTimeAlertEventsCheckbox'
import { MRT_ColumnDef } from 'material-react-table'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
    Column,
    IGeneralReportView,
    IGenerateReportAPIResponse,
    ISelectedAttributes,
} from '../../interfaces'
import { formatDateTimeWithUserPreferences } from '../../../../utils/DateAndTimeUtils'
import { SnackbarKey, useSnackbar } from 'notistack'
import MRTTable from '../../../Common/Table'
import {
    DetailData,
    ITimeSeriesDataTableAPIResponse,
} from '../../../../services/apiInterfaces'
import { IAttribute } from '../../../DTStudio/interfaces'
import { useTranslation } from 'react-i18next'
import {
    ReportGenerationStatus,
    updateReportGenerationDetails,
} from '../../../../redux/slices/entityViewSlice'
import { getAttributeById } from '../../../../utils/AttributeUtils'
dayjs.extend(utc)

const GeneralReportView = (props: IGeneralReportView): ReactElement => {
    const {
        reportSettings,
        selfEntityAttributeData,
        orgId,
        startDate,
        endDate,
        entityInstanceCreatedDate,
        disableGenerate,
        selectedAttributes,
        includeStage,
    } = props
    const {
        reportedEntityTemplateId,
        reportsEntityTemplateId,
        reportDataFromAttributeId, //Part of Reports attributes
        reportDataUntilAttributeId, //Part of Reports attributes
        reportURLAttributeId, //Part of Reports attributes
        reportingObjectAttributeId, //Part of Reports attributes
        reportIdAttributeId, //Part of Shipment attributes
    } = reportSettings
    const isReportsConfigured =
        !!reportedEntityTemplateId &&
        !!reportsEntityTemplateId &&
        !!reportDataFromAttributeId &&
        !!reportDataUntilAttributeId &&
        !!reportURLAttributeId &&
        !!reportingObjectAttributeId &&
        !!reportIdAttributeId
    const { t } = useTranslation()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const queryClient = useQueryClient()
    const dispatch = useAppDispatch()
    const navSlice = useAppSelector((state) => state.nav)
    const templateName =
        findEntityById(navSlice.navList, reportedEntityTemplateId).name || ''
    const org = useAppSelector((state) => state.org.org)
    const user = useAppSelector((state) => state.user.user)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const [filterTriggeredReadings, setFilterTriggeredReadings] = useState(
        org?.preferences?.data?.excludeTriggeredEvents === 'true',
    )
    useEffect(() => {
        if (org?.id) {
            setFilterTriggeredReadings(
                org?.preferences?.data?.excludeTriggeredEvents === 'true',
            )
        }
    }, [org?.id])
    const handleFilterTriggeredReadingsChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) =>
            setFilterTriggeredReadings(!e.target.checked),
        [],
    )
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const canCreateReports = !(isGuestUser || disableGenerate)
    const reportEntityInstanceIdFromPreview: string | undefined =
        getAttributeById(
            selfEntityAttributeData.attributes,
            reportIdAttributeId,
        )?.value
    const reportEntityInstanceIdInStore = useAppSelector(
        (state) =>
            state.entityInstanceView.reportGeneration.reportEntityInstanceId,
    )
    const isReportGenerationInProgress = useAppSelector(
        (state) =>
            state.entityInstanceView.reportGeneration.reportGenerationStatus ===
            ReportGenerationStatus.IN_PROGRESS,
    )
    const reportEntityInstanceId =
        reportEntityInstanceIdFromPreview || reportEntityInstanceIdInStore || ''
    const entityId = selfEntityAttributeData.entityId
    const lastSecondOfDay = dayjs(new Date()).utc().endOf('day').format()

    const [lastGeneralReportGenerated, setLastGeneralReportGenerated] =
        useState(localStorage.getItem('lastGeneralReportGenerated') || '')
    const disableGenerateReportButton =
        !startDate ||
        !endDate ||
        lastGeneralReportGenerated === `${startDate}_to_${endDate}`

    const queryKey = useMemo(
        () => ['chart', orgId, reportEntityInstanceId, user],
        [orgId, reportEntityInstanceId, user],
    )

    const refetechReports = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey,
        })
    }, [queryKey, queryClient])

    const shipmentAttributeIdValue =
        getAttributeById(selfEntityAttributeData.attributes, 'Id')?.value ||
        selfEntityAttributeData.uid

    const onGenerateReport = useCallback(async () => {
        const assetId = {
            templateId: reportedEntityTemplateId,
            templateName,
            entityId,
            uid: selfEntityAttributeData.uid,
        }
        const selectedAttributeKeys = selectedAttributes.map(
            (attribute) => attribute.attribute.id,
        )

        const payload = {
            attributeValues: {
                Id: `${shipmentAttributeIdValue}+Report`, //This concatenation is to ensure uniqueness for Report Id.
                [reportingObjectAttributeId]: assetId,
                [reportDataFromAttributeId]: startDate,
                [reportDataUntilAttributeId]: endDate,
                frequency: 'onDemandOnly',
                selectedAttributes: selectedAttributeKeys,
                filterTriggeredReadings,
            },
            ruleSetId: null,
            status: 'ACTIVE',
        }
        let snackbarKey: SnackbarKey | undefined = undefined

        try {
            snackbarKey = enqueueSnackbar(t('reportGenerationInProgress'), {
                variant: 'info',
                persist: true,
            })
            dispatch(
                updateReportGenerationDetails({
                    reportGenerationStatus: ReportGenerationStatus.IN_PROGRESS,
                }),
            )
            const response: IGenerateReportAPIResponse =
                await generatePDFReportForEntity(
                    orgId,
                    reportsEntityTemplateId,
                    reportEntityInstanceId || '0',
                    payload,
                    org?.id || '',
                    includeStage || false,
                )
            const lastGeneralReportGenerated = `${startDate}_to_${endDate}`
            localStorage.setItem(
                'lastGeneralReportGenerated',
                lastGeneralReportGenerated,
            )
            setLastGeneralReportGenerated(lastGeneralReportGenerated)
            refetechReports()
            closeSnackbar(snackbarKey)
            /**
             * There is remote chance of displaying an unwarranted spinner when the user immediate jumps to another instance preview page.
             * This can happen when the report generation takes more than 15 seconds are so.
             * To handle the scenario we need to maintain reportGeneration: IReportGeneration[] (as an array). Each object in it now should
             * also include reportedEntityTemplateId to uniquely identify a entity instance preview page. As of now this scenario is very
             * unlikey. Hence omitting this scenario.
             */
            dispatch(
                updateReportGenerationDetails({
                    reportEntityInstanceId: response.entityId,
                    reportGenerationStatus: ReportGenerationStatus.SUCCESS,
                }),
            )
            enqueueSnackbar(t('reportGenerationSuccess'), {
                variant: 'success',
            })
        } catch {
            if (snackbarKey) {
                closeSnackbar(snackbarKey)
            }
            dispatch(
                updateReportGenerationDetails({
                    reportEntityInstanceId,
                    reportGenerationStatus: ReportGenerationStatus.FAILED,
                }),
            )
            enqueueSnackbar(t('reportGenerationError'), { variant: 'error' })
        }
    }, [
        closeSnackbar,
        enqueueSnackbar,
        selectedAttributes,
        dispatch,
        endDate,
        entityId,
        filterTriggeredReadings,
        org?.id,
        orgId,
        refetechReports,
        reportDataFromAttributeId,
        reportDataUntilAttributeId,
        reportEntityInstanceId,
        reportedEntityTemplateId,
        reportingObjectAttributeId,
        reportsEntityTemplateId,
        selfEntityAttributeData.uid,
        shipmentAttributeIdValue,
        startDate,
        t,
        templateName,
    ])

    const timeSeriesAttributes: ISelectedAttributes[] = useMemo(
        () => [
            {
                entityInstanceId: reportEntityInstanceId || '',
                attribute: {
                    id: reportURLAttributeId,
                    dataType: AttributeType.URL.toUpperCase(),
                } as IAttribute,
            },
        ],
        [reportEntityInstanceId, reportURLAttributeId],
    )

    const chartAPIOptions = useMemo(
        () => ({
            enabled: isReportsConfigured, // Query only runs if true
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        }),
        [isReportsConfigured],
    )

    const {
        data,
        fetchNextPage,
        isError,
        isFetching: isFetchingReports,
        isLoading: isLoadingReports,
    } = useInfiniteQuery<ITimeSeriesDataTableAPIResponse>({
        queryKey,
        queryFn: async ({ pageParam }) => {
            const response = await fetchTimeseriesTableData({
                viewID: reportEntityInstanceId || '',
                selectedAttributes: timeSeriesAttributes,
                startDate: entityInstanceCreatedDate,
                endDate: lastSecondOfDay,
                orgId,
                pageNumber: (pageParam as number) + 1,
                pageSize: 20,
                filterOutlierLocations: false,
                isGuestUser,
                filterOutNonTimeSeriesData: false,
                sortBy: 'DESC', //Always DESC order for PDF Reports
                filterLightTriggered: filterTriggeredReadings,
                filterShockTriggered: filterTriggeredReadings,
            })
            return response as ITimeSeriesDataTableAPIResponse
        },
        ...chartAPIOptions,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            // Check if we got any data
            if (!lastPage?.details?.data?.length) {
                return undefined
            }

            // Calculate if we've received all data
            const totalFetched = pages.reduce(
                (acc, page) => acc + (page?.details?.data?.length || 0),
                0,
            )
            if (totalFetched >= (lastPage?.details?.totalRowCount || 0)) {
                return undefined
            }

            return pages.length
        },
        refetchOnWindowFocus: false,
        enabled: !!timeSeriesAttributes && !!startDate && !!endDate,
    })

    const flatData = useMemo(
        () => data?.pages?.flatMap((page) => page?.details?.data) ?? [],
        [data],
    )

    const totalDBRowCount = data?.pages?.[0]?.details?.totalRowCount ?? 0

    const columns: MRT_ColumnDef<DetailData>[] = useMemo(
        () =>
            data?.pages?.[0]?.details?.columns.map((col: Column) => ({
                accessorKey: col.name, // access the column dynamically
                header: col.name.toLowerCase().includes('url')
                    ? t('report')
                    : t(col.name), // column name
                Cell: ({ cell }: { cell: any }) => {
                    if (col.type === 'URL') {
                        return (
                            <a
                                href={cell.getValue()}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: 'none',
                                }}
                            >
                                {t('clickHere')}
                            </a>
                        )
                    } else if (col.type === 'DateTime') {
                        return (
                            <span>
                                {formatDateTimeWithUserPreferences(
                                    cell.getValue(),
                                    userLocalization,
                                )}
                            </span>
                        )
                    } else {
                        return <span>{cell.getValue()}</span>
                    }
                },
            })) ?? [],
        [data?.pages, t, userLocalization],
    )

    return (
        <Box sx={{ height: '100%', width: '100%', padding: '16px' }}>
            {isReportsConfigured ? (
                <>
                    {canCreateReports && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                marginBottom: '16px',
                            }}
                        >
                            <RealTimeAlertEventsCheckbox
                                checked={!filterTriggeredReadings}
                                onChange={handleFilterTriggeredReadingsChange}
                                sx={{ mr: 1 }}
                            />
                            {disableGenerateReportButton && (
                                <Tooltip
                                    title={t('pleaseChangeDateTime')}
                                    disableInteractive={
                                        !disableGenerateReportButton
                                    } // Only show tooltip when disabled
                                >
                                    <span>
                                        <Button
                                            id="generate-report-button"
                                            sx={{
                                                marginRight: '10px',
                                                backgroundColor: 'inherit',
                                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                                textTransform: 'none',
                                                fontWeight:
                                                    isReportGenerationInProgress
                                                        ? 'bold'
                                                        : 500,
                                            }}
                                            onClick={onGenerateReport}
                                            disabled={
                                                disableGenerateReportButton
                                            }
                                        >
                                            {t('generateReport')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {!disableGenerateReportButton && (
                                <Button
                                    id="generate-report-button"
                                    sx={{
                                        marginRight: '10px',
                                        backgroundColor: 'inherit',
                                        border: '1px solid rgba(0, 0, 0, 0.3)',
                                        textTransform: 'none',
                                        fontWeight: isReportGenerationInProgress
                                            ? 'bold'
                                            : 500,
                                    }}
                                    onClick={onGenerateReport}
                                    loading={isReportGenerationInProgress}
                                    loadingPosition="end"
                                    disabled={disableGenerateReportButton}
                                >
                                    {t('generateReport')}
                                </Button>
                            )}
                        </Box>
                    )}
                    {isFetchingReports && isLoadingReports ? (
                        <LinearProgress />
                    ) : columns?.length > 0 ? (
                        <Box
                            sx={{
                                overflow: 'auto',
                            }}
                        >
                            <MRTTable<
                                DetailData,
                                ITimeSeriesDataTableAPIResponse
                            >
                                columns={columns}
                                sorting={[]} // Adjust sorting state if needed
                                globalFilter={''} // Adjust if search functionality is needed
                                columnFilters={[]} // Adjust filters if needed
                                setColumnFilters={() => {}} // Provide state update function if needed
                                setGlobalFilter={() => {}} // Provide state update function if needed
                                setSorting={() => {}} // Provide state update function if needed
                                data={flatData ?? []}
                                totalDBRowCount={totalDBRowCount}
                                fetchNextPage={fetchNextPage}
                                isError={isError}
                                isFetching={isFetchingReports}
                                isLoading={isLoadingReports}
                                enableBottomToolbar={true}
                                isCurvedBottomToolbar={false}
                                bottomToolbarPadding={'0px'}
                                enableRowSelection={false}
                                enableSorting={false}
                                maxHeight="400px"
                            />
                        </Box>
                    ) : (
                        <Typography
                            sx={{
                                fontStyle: 'italic',
                                textAlign: 'center',
                                color: 'rgba(0, 0, 0, 0.6)',
                                paddingBottom: '20px',
                            }}
                        >
                            {t('noRecordsToDisplay')}
                        </Typography>
                    )}
                </>
            ) : (
                <Typography
                    sx={{
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.6)',
                        paddingBottom: '20px',
                    }}
                >
                    {t('reportsNotConfigured')}
                </Typography>
            )}
        </Box>
    )
}

export default GeneralReportView
